import React from 'react'
import styled from 'styled-components'
import { BigNumber } from 'ethers'
import { Colors, DISCORD_URL } from '../../constants'
import { formatCurrency } from '../../model'
import { TokenRewards } from '../../providers/apiTokenRewards/model'
import { bigNumber18DecimalsToDollars, bigNumber18DecimalsToPercent, BN_10_18, timeLeft } from '../../utils'
import { Spinner } from '../Spinner'

const ONE_HUNDRED_PERCENT = BigNumber.from(100).mul(BN_10_18)

interface Props {
  className?: string
  apiTokenRewards?: TokenRewards
}

export function TradeRewardsProgressBar({ className, apiTokenRewards }: Props) {
  let state: 'loading' | 'enabled' | 'disabled' | 'not-whitelisted' = 'loading'
  let volume = '-'
  let limit = '-'
  let rawPercent = BigNumber.from(0)
  let percent = '-'
  let remaining = '-'
  if (apiTokenRewards) {
    const daily = apiTokenRewards.tradeRewards.daily
    if (daily) {
      state = daily.limit.isZero() ? 'not-whitelisted' : 'enabled'
      volume = bigNumber18DecimalsToDollars(daily.volume)
      limit = bigNumber18DecimalsToDollars(daily.limit)
      rawPercent = daily.limit.isZero() ? BigNumber.from(0) : daily.volume.mul(BN_10_18).div(daily.limit)
      if (rawPercent.gt(ONE_HUNDRED_PERCENT)) {
        rawPercent = ONE_HUNDRED_PERCENT
      }
      percent = bigNumber18DecimalsToPercent(rawPercent)
      remaining = timeLeft(new Date(), daily.end)
    } else {
      state = 'disabled'
    }
  }

  if (state == 'loading') {
    return (
      <Container className={className}>
        <Loading>
          <Spinner />
        </Loading>
      </Container>
    )
  } else if (state == 'not-whitelisted') {
    return (
      <Container className={className}>
        <P>
          Your account has not been whitelisted to participate in trade rewards. Learn how to participate{' '}
          <a href="https://blog.integral.link/p/integral-launches-million-dollar" target="_blank">
            here
          </a>
          .
        </P>
      </Container>
    )
  } else if (state == 'disabled') {
    return (
      <Container className={className}>
        <P>
          Trade mining is currently disabled. Check back later or visit our{' '}
          <a href={DISCORD_URL} target="_blank">
            Discord
          </a>{' '}
          for details on our next event.
        </P>
      </Container>
    )
  }

  return (
    <Container className={className}>
      <Volume>
        <strong>Daily Volume</strong>
        {volume} / {limit}
      </Volume>
      <BarContainer>
        <Bar>
          <Fill percent={formatCurrency(16, rawPercent)} />
        </Bar>
        <Percent>{percent}</Percent>
      </BarContainer>
      <TimeLeft>
        <strong>Time Left</strong>
        {remaining}
      </TimeLeft>
    </Container>
  )
}

const Container = styled.div`
  margin: 15px 5px;
  font-size: 12px;
  color: ${Colors.DarkerGray};
`

const Volume = styled.div`
  strong {
    margin-right: 10px;
    font-size: 11px;
    color: ${Colors.Black};
  }
`

const BarContainer = styled.div`
  display: flex;
  align-items: center;
`

const Bar = styled.div`
  margin: 5px 0;
  display: flex;
  flex-basis: 100%;
  background-color: ${Colors.LightestGray};
  border: 1px solid ${Colors.LightGray};
`

const Fill = styled.div.attrs((props: { percent: string }) => ({
  percent: props.percent,
}))`
  flex-basis: ${(props) => props.percent}%;
  height: 20px;
  background: rgb(56, 140, 211);
  background: linear-gradient(0deg, rgba(56, 140, 211, 1) 0%, rgba(23, 76, 178, 1) 100%);
  border: 1px solid #143980;
`

const Percent = styled.strong`
  margin-left: 10px;
  color: ${Colors.Black};
  font-size: 13px;
`

const TimeLeft = styled.div`
  font-size: 10px;

  strong {
    margin-right: 10px;
    font-size: 9px;
    color: ${Colors.Black};
  }
`

const Loading = styled.div`
  display: flex;
  justify-content: center;
`

const P = styled.p`
  text-transform: initial;
`
